import type { MouseEventHandler, ReactEventHandler, ReactNode, Ref } from 'react';
import { ModalTitle } from './ModalTitle';
import type { ModalIconKind } from './ModalIcon';

export interface DialogProps {
  id?: string;
  /** The title's glyph from the shared icon map (`'alert'` for an error). Required unless `mark` is given. */
  icon?: ModalIconKind;
  /** A brand tile in the title's icon slot instead of a glyph (an integration's own dialog). */
  mark?: ReactNode;
  title: ReactNode;
  titleId: string;
  /** The reason line's id: it describes the dialog to a screen reader. */
  descriptionId?: string;
  closeId?: string;
  onClose?: () => void;
  /** The actions, secondary first and primary last: `oq-btn` links or buttons. */
  footer?: ReactNode;
  children: ReactNode;
  dialogRef?: Ref<HTMLDialogElement>;
  onCancel?: ReactEventHandler<HTMLDialogElement>;
  onClick?: MouseEventHandler<HTMLDialogElement>;
  testId?: string;
  /** The width tier (dialog.css): `compact`, 24rem, the default, for a message or a confirm; `standard`, 28rem, for a
   *  dialog that carries content; `wide`, 36rem, for setup steps. */
  width?: 'compact' | 'standard' | 'wide';
}

/**
 * The shared modal shell (dialog.css): a native <dialog>, so the opener's showModal() traps focus, makes the page
 * behind it inert and closes on Escape. The header, body and footer fill the dialog, so a click whose target is the
 * dialog element itself landed on the backdrop. The server-rendered review page renders this same component to
 * static markup and wires it with an inline script.
 */
export function Dialog({ id, icon, mark, title, titleId, descriptionId, closeId, onClose, footer, children, dialogRef, onCancel, onClick, testId, width = 'compact' }: DialogProps) {
  return (
    <dialog ref={dialogRef} id={id} className={width === 'compact' ? 'oq-dialog' : `oq-dialog oq-dialog--${width}`} aria-labelledby={titleId} aria-describedby={descriptionId}
      data-testid={testId} onCancel={onCancel} onClick={onClick}>
      <header className="oq-dialog__header">
        <ModalTitle icon={icon} mark={mark} id={titleId} className="oq-dialog__title">{title}</ModalTitle>
        <button type="button" id={closeId} className="oq-dialog__close" aria-label="Close" onClick={onClose}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12" /></svg>
        </button>
      </header>
      <div className="oq-dialog__body">{children}</div>
      {footer && <footer className="oq-dialog__footer">{footer}</footer>}
    </dialog>
  );
}

/** The dialog's message: what happened and what to do. */
export function DialogReason({ id, children }: { id?: string; children?: ReactNode }) {
  return <p id={id} className="oq-dialog__reason">{children}</p>;
}

/** A smaller grey line under the reason, such as the address that failed. */
export function DialogDetail({ children }: { children: ReactNode }) {
  return <p className="oq-dialog__detail">{children}</p>;
}
